/**
 * @file useInteractionMessagesPopover.js
 * @description NavBar 互动消息铃铛弹层：分页列表、已读、跳转详情/阅读页
 * @module features/message/composables/useInteractionMessagesPopover
 */

import {
  computed,
  nextTick,
  onBeforeUnmount,
  ref,
  watch,
} from 'vue'
import { useRouter } from 'vue-router'
import { convertRouteParams } from '@/shared/utils/idHelper'
import { isMobileLayoutViewport } from '@/shared/utils/mobileLayout'
import { getOssImageUrl } from '@/shared/utils/ossImage'
import {
  fetchInteractionPageApi,
  markInteractionReadApi,
} from '@/features/message/api/messageApi'

/** 互动消息里用于定位评论/回复的 id */
function pickInteractionHighlightCommentId(row) {
  if (!row || typeof row !== 'object') return ''
  const keys = [
    'replyMessageId',
    'myCommentId',
    'targetMessageId',
    'messageId',
    'commentId',
    'targetId',
    'refMessageId',
    'interactionSourceMessageId',
    'anchorMessageId',
    'relatedMessageId',
    'parentMessageId',
  ]
  for (const k of keys) {
    const v = row[k]
    if (v !== undefined && v !== null && String(v).trim() !== '') {
      return String(v).trim()
    }
  }
  return ''
}

/**
 * @param {import('vue').Ref<boolean>} visible
 * @param {import('vue').Ref<boolean>} isMobile
 * @param {() => HTMLElement | null} getAnchor
 * @param {(event: string, ...args: unknown[]) => void} emit
 */
export function useInteractionMessagesPopover(visible, isMobile, getAnchor, emit) {
  const router = useRouter()

  const panelRef = ref(null)
  const impScroll = ref(null)
  const listSentinel = ref(null)

  const accumulatedList = ref([])
  const currentPage = ref(1)
  const pageSize = ref(10)
  const totalItems = ref(0)
  const token = ref('')
  const loading = ref(false)
  const loadingMore = ref(false)
  const interactionListFullyLoaded = ref(false)
  const interactionUserScrolledList = ref(false)
  const panelTop = ref(72)
  const panelRight = ref(16)
  const bodyScrollLocked = ref(false)
  const lockedScrollY = ref(0)

  let infiniteObserver = null

  const displayList = computed(() => accumulatedList.value)

  const listNoMore = computed(() => {
    if (totalItems.value === 0) return true
    return accumulatedList.value.length >= totalItems.value
  })

  const showInfiniteEndTip = computed(() => {
    const ps = pageSize.value
    if (totalItems.value <= ps) return false
    if (loading.value || loadingMore.value) return false
    if (displayList.value.length === 0) return false
    return interactionListFullyLoaded.value
  })

  const panelStyle = computed(() => {
    if (isMobile.value) {
      return {
        top: `${panelTop.value}px`,
        left: '8px',
        right: '8px',
        width: 'auto',
      }
    }
    return {
      top: `${panelTop.value}px`,
      right: `${panelRight.value}px`,
      left: 'auto',
    }
  })

  function syncInteractionPageSize() {
    pageSize.value = isMobile.value ? 5 : 10
  }

  function onWindowResizeWhileOpen() {
    if (visible.value) syncPanelPosition()
  }

  function onResizePosition() {
    if (visible.value) syncPanelPosition()
  }

  function syncPanelPosition() {
    const el = typeof getAnchor === 'function' ? getAnchor() : null
    if (el && el.getBoundingClientRect) {
      const r = el.getBoundingClientRect()
      panelTop.value = r.bottom + 8
      panelRight.value = Math.max(8, window.innerWidth - r.right)
    } else {
      panelTop.value = 72
      panelRight.value = 16
    }
  }

  function onEscape(e) {
    if (e.key === 'Escape') close()
  }

  function close() {
    emit('update:visible', false)
    emit('close')
  }

  function onBackdropClick() {
    if (isMobile.value) close()
  }

  function onPanelMouseEnter() {
    emit('panel-enter')
  }

  function onPanelMouseLeave() {
    emit('panel-leave')
  }

  function lockBodyScroll() {
    if (bodyScrollLocked.value) return
    bodyScrollLocked.value = true
    lockedScrollY.value = window.scrollY || window.pageYOffset || 0
    document.body.style.top = `-${lockedScrollY.value}px`
    document.body.style.position = 'fixed'
    document.body.style.width = '100%'
    document.body.style.overflow = 'hidden'
  }

  function unlockBodyScroll() {
    if (!bodyScrollLocked.value) return
    bodyScrollLocked.value = false
    const y = lockedScrollY.value || 0
    document.body.style.position = ''
    document.body.style.top = ''
    document.body.style.width = ''
    document.body.style.overflow = ''
    window.scrollTo(0, y)
    lockedScrollY.value = 0
  }

  function unbindInfiniteObserver() {
    if (infiniteObserver) {
      infiniteObserver.disconnect()
      infiniteObserver = null
    }
  }

  function bindInfiniteObserver() {
    unbindInfiniteObserver()
    if (!visible.value || typeof IntersectionObserver === 'undefined') return
    if (!interactionUserScrolledList.value) return

    nextTick(() => {
      const root = impScroll.value
      const el = listSentinel.value
      if (!root || !el || listNoMore.value || loading.value || loadingMore.value) {
        return
      }

      infiniteObserver = new IntersectionObserver(
        (entries) => {
          if (
            !entries[0]?.isIntersecting ||
            listNoMore.value ||
            loadingMore.value ||
            loading.value ||
            !interactionUserScrolledList.value
          ) {
            return
          }
          fetchList(true)
        },
        { root, rootMargin: '0px', threshold: 0 }
      )
      infiniteObserver.observe(el)
    })
  }

  function onImpScrollList() {
    if (interactionUserScrolledList.value) return
    interactionUserScrolledList.value = true
    nextTick(() => bindInfiniteObserver())
  }

  function resolveRequestPageNum(append) {
    if (append) return currentPage.value + 1
    return 1
  }

  function resolveCover(url) {
    return getOssImageUrl(url, 'thumb')
  }

  function formatInteractionLine(item) {
    if (item.interactionType === 'REPLY') {
      return `回复：${item.content || ''}`
    }
    return item.content || ''
  }

  /**
   * 跳转反馈详情（FEEDBACK_REPLY）
   * @param {string} feedbackId
   */
  function openFeedbackDetail(feedbackId) {
    const id = feedbackId != null ? String(feedbackId).trim() : ''
    if (!id) return
    if (isMobileLayoutViewport()) {
      router.push({
        name: 'UserModule',
        params: { module: 'UserFeedback' },
        query: { feedbackId: id },
      })
      return
    }
    router.push({
      name: 'UserCenter',
      query: { module: 'UserFeedback', feedbackId: id },
    })
  }

  async function fetchList(append) {
    if (!visible.value) return

    if (append) {
      if (
        loadingMore.value ||
        listNoMore.value ||
        accumulatedList.value.length >= totalItems.value
      ) {
        return
      }
    }

    if (!token.value) return

    const pageNum = resolveRequestPageNum(append)
    if (!append) {
      loading.value = true
      interactionListFullyLoaded.value = false
      interactionUserScrolledList.value = false
    } else {
      loadingMore.value = true
    }

    let requestOk = false
    try {
      const response = await fetchInteractionPageApi(
        { pageNum, pageSize: pageSize.value },
        token.value
      )

      if (response.data.code === 200 || response.data.code === 0) {
        requestOk = true
        const raw = response.data.contents
        const list = Array.isArray(raw) ? raw : []
        const total = Number(response.data.total) || 0
        const mapped = list.map((row) => ({
          ...row,
          interactionMessageId: String(row.interactionMessageId),
          fromUserId: String(row.fromUserId),
          fromUserName: row.fromUserName,
          fromUserAvatar: row.fromUserAvatar,
          interactionType: row.interactionType,
          content: row.content,
          comicId: row.comicId,
          comicCover: resolveCover(row.comicCover),
          volId: row.volId,
          myCommentId: row.myCommentId,
          feedbackId:
            row.feedbackId != null && String(row.feedbackId).trim() !== ''
              ? String(row.feedbackId)
              : '',
          highlightCommentId: pickInteractionHighlightCommentId(row),
          read: row.read,
          interactionTime: row.interactionTime,
        }))
        totalItems.value = total

        if (!append) {
          accumulatedList.value = [...mapped]
          currentPage.value = 1
        } else if (mapped.length === 0) {
          currentPage.value = pageNum
        } else {
          accumulatedList.value.push(...mapped)
          currentPage.value = pageNum
        }
      } else {
        console.error('获取互动消息失败:', response.data.msg)
      }
    } catch (error) {
      console.error('互动消息请求错误:', error)
    } finally {
      loading.value = false
      loadingMore.value = false
      if (requestOk && totalItems.value > 0) {
        interactionListFullyLoaded.value =
          accumulatedList.value.length >= totalItems.value
      } else {
        interactionListFullyLoaded.value = false
      }
      nextTick(() => bindInfiniteObserver())
    }
  }

  async function postInteractionRead(interactionMessageId) {
    if (!token.value) return
    const id =
      interactionMessageId === undefined || interactionMessageId === null
        ? ''
        : String(interactionMessageId)
    try {
      const res = await markInteractionReadApi(id, token.value)
      if (res.data.code === 0 || res.data.code === 200) {
        emit('unread-sync')
      }
    } catch (e) {
      console.error('标记互动消息已读失败:', e)
    }
  }

  async function handleMarkAllReadClick() {
    if (!token.value) return
    try {
      const response = await markInteractionReadApi('', token.value)
      if (response.data.code === 0 || response.data.code === 200) {
        accumulatedList.value = accumulatedList.value.map((row) => ({
          ...row,
          read: true,
        }))
        emit('unread-sync')
      }
    } catch (e) {
      console.error('标记全部已读失败:', e)
    }
  }

  async function openComic(item) {
    const feedbackId =
      item?.feedbackId != null ? String(item.feedbackId).trim() : ''
    const isFeedbackReply =
      item?.interactionType === 'FEEDBACK_REPLY' ||
      (!item?.comicId && Boolean(feedbackId))

    if (isFeedbackReply) {
      if (!feedbackId) return
      await postInteractionRead(item.interactionMessageId)
      close()
      openFeedbackDetail(feedbackId)
      return
    }

    if (!item?.comicId) return
    await postInteractionRead(item.interactionMessageId)
    close()

    const params = convertRouteParams({ id: item.comicId })
    const highlightId =
      (item.highlightCommentId && String(item.highlightCommentId).trim()) ||
      pickInteractionHighlightCommentId(item)
    const volIdRaw = item.volId
    const hasVol =
      volIdRaw !== undefined &&
      volIdRaw !== null &&
      String(volIdRaw).trim() !== ''

    const query = {}
    if (highlightId) {
      query.highlightComment = highlightId
    }

    if (!hasVol) {
      router.push({
        name: 'ComicDetail',
        params: { id: params.id },
        query,
      })
      return
    }

    router.push({
      name: 'ComicReader',
      params: convertRouteParams({
        comicId: params.id,
        volNum: 1,
      }),
      query: {
        targetVolId: String(volIdRaw).trim(),
        ...query,
      },
    })
  }

  function onPanelOpen() {
    token.value = JSON.parse(localStorage.getItem('user'))?.token || ''
    syncInteractionPageSize()
    currentPage.value = 1
    accumulatedList.value = []
    interactionListFullyLoaded.value = false
    interactionUserScrolledList.value = false
    if (isMobile.value) lockBodyScroll()
    nextTick(() => {
      syncPanelPosition()
      fetchList(false)
      bindInfiniteObserver()
    })
    window.addEventListener('resize', onWindowResizeWhileOpen)
    window.addEventListener('scroll', onResizePosition, true)
    document.addEventListener('keydown', onEscape)
  }

  function onPanelClose() {
    unbindInfiniteObserver()
    unlockBodyScroll()
    window.removeEventListener('resize', onWindowResizeWhileOpen)
    window.removeEventListener('scroll', onResizePosition, true)
    document.removeEventListener('keydown', onEscape)
  }

  watch(visible, (val) => {
    if (val) {
      onPanelOpen()
    } else {
      onPanelClose()
    }
  })

  watch(isMobile, () => {
    if (!visible.value) return
    syncInteractionPageSize()
    currentPage.value = 1
    accumulatedList.value = []
    interactionListFullyLoaded.value = false
    interactionUserScrolledList.value = false
    nextTick(() => {
      fetchList(false)
      bindInfiniteObserver()
    })
  })

  onBeforeUnmount(() => {
    onPanelClose()
  })

  return {
    panelRef,
    impScroll,
    listSentinel,
    token,
    loading,
    loadingMore,
    displayList,
    showInfiniteEndTip,
    panelStyle,
    close,
    onBackdropClick,
    onPanelMouseEnter,
    onPanelMouseLeave,
    onImpScrollList,
    formatInteractionLine,
    handleMarkAllReadClick,
    openComic,
  }
}
